import { useMemo, useRef, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AksaraDrawCanvas from "../components/aksara/AksaraDrawCanvas.tsx";
import QuestionPageLayout from "../components/question/QuestionPageLayout.tsx";
import { completeAksaraFromUI, getUserData } from "../utils/userStorage.ts";

const AksaraLearnPage = ({ hearts, onBack }) => {
  const canvasRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const data = getUserData();
    return data?.meta?.darkMode ?? false;
  });

  // Listen for localStorage changes to keep darkMode in sync
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "galaxy_aksara_user_data") {
        const data = getUserData();
        setDarkMode(data?.meta?.darkMode ?? false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const batakChar = useMemo(() => {
    const rawBatakChar = location.state?.batakChar || { char: "ᯀ", latin: "a" };
    function pickRandomChar(char) {
      if (typeof char === "string" && char.includes("/")) {
        const options = char.split("/");
        return options[Math.floor(Math.random() * options.length)];
      }
      return char;
    }
    return {
      ...rawBatakChar,
      char: pickRandomChar(rawBatakChar.char),
    };
  }, [location.state?.batakChar]);

  const [checked, setChecked] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [similarityScore, setSimilarityScore] = useState<number | null>(null);
  const [hasStroke, setHasStroke] = useState(false);

  // Called by canvas when check is triggered
  const handleResult = ({ similarity, isCorrect }) => {
    setChecked(true);
    setIsCorrect(isCorrect);
    setSimilarityScore(similarity);
  };

  // Check button
  const handleCheck = () => {
    if (checked && isCorrect) {
      // Save progress when user completes aksara successfully
      const charKey = batakChar.chars
        ? batakChar.chars.join("/")
        : batakChar.char;
      completeAksaraFromUI(charKey, 5); // 5 money reward for completing aksara
      navigate("/aksara");
      return;
    }
    if (checked && !isCorrect) {
      setChecked(false);
      setIsCorrect(null);
      setSimilarityScore(null);
      if (canvasRef.current) canvasRef.current.reset();
      return;
    }
    if (canvasRef.current) {
      canvasRef.current.check();
    }
  };

  // Reset button
  const handleReset = () => {
    if (checked) return;
    setChecked(false);
    setIsCorrect(null);
    setSimilarityScore(null);
    if (canvasRef.current) canvasRef.current.reset();
  };

  // Progress bar logic
  const progressValue = checked && isCorrect ? 1 : 0;

  return (
    <QuestionPageLayout
      hearts={hearts || 5}
      progress={progressValue}
      onBack={onBack}
      darkMode={darkMode}
    >
      {/* Character & Pronunciation */}
      <div className="flex flex-col items-center justify-center px-8 pt-6 pb-2">
        <span
          className={`text-lg ${darkMode ? "text-gray-300" : "text-gray-500"}`}
          style={{ fontFamily: "DIN Next Rounded" }}
        >
          {batakChar.latin}
        </span>
        <span
          className={`text-4xl font-base mb-2 ${
            darkMode ? "text-gray-100" : "text-gray-700"
          }`}
          style={{ fontFamily: "Noto Sans Batak" }}
        >
          {batakChar.char}
        </span>
      </div>
      {/* Canvas Area */}
      <div className="flex-1 flex items-center justify-center">
        <div className="relative">
          <AksaraDrawCanvas
            ref={canvasRef}
            char={batakChar.char}
            checked={checked}
            onResult={handleResult}
            onStrokeChange={setHasStroke}
            darkMode={darkMode}
          />
        </div>
      </div>
      {/* Check & Reset Button */}
      <div className="px-8 pb-8 flex flex-col gap-3">
        <div
          className="mb-4 min-h-[64px] flex items-center justify-center"
          style={{ fontFamily: "DIN Next Rounded" }}
        >
          {checked ? (
            <div
              className={`text-center mt-10 text-lg font-semibold ${
                isCorrect
                  ? darkMode
                    ? "text-lime-400"
                    : "text-lime-600"
                  : darkMode
                  ? "text-red-400"
                  : "text-red-500"
              }`}
            >
              {isCorrect
                ? "Jawaban benar! Tulisan Anda mirip dengan aksara panduan."
                : "Jawaban salah. Silakan coba lagi mengikuti bentuk aksara."}
              <div
                className={`mt-2 text-base ${
                  darkMode ? "text-gray-300" : "text-gray-500"
                }`}
              >
                Kemiripan:{" "}
                {similarityScore !== null
                  ? (similarityScore * 100).toFixed(1) + "%"
                  : "-"}
              </div>
            </div>
          ) : (
            <div className="text-center mt-10 text-lg text-transparent">
              blank
              <div className="mt-2 text-base text-transparent">blank</div>
            </div>
          )}
        </div>
        <button
          className={`w-full py-3 rounded-xl font-semibold text-lg transition ${
            !checked && hasStroke
              ? darkMode
                ? "bg-blue-700 text-white hover:bg-blue-800"
                : "bg-blue-500 text-white hover:bg-blue-600"
              : checked && isCorrect
              ? darkMode
                ? "bg-lime-600 text-white"
                : "bg-lime-500 text-white"
              : checked && !isCorrect
              ? darkMode
                ? "bg-red-700 text-white"
                : "bg-red-500 text-white"
              : darkMode
              ? "bg-gray-700 text-gray-400 cursor-not-allowed"
              : "bg-gray-300 text-gray-400 cursor-not-allowed"
          }`}
          style={{ fontFamily: "DIN Next Rounded" }}
          disabled={!checked && !hasStroke}
          onClick={handleCheck}
        >
          {checked ? (isCorrect ? "Selesai" : "Ulangi") : "Periksa"}
        </button>
        <button
          className={`w-full py-2 rounded-xl font-semibold text-md transition ${
            !checked && hasStroke
              ? darkMode
                ? "bg-gray-800 text-gray-200 hover:bg-gray-700"
                : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              : darkMode
              ? "bg-gray-700 text-gray-400 cursor-not-allowed"
              : "bg-gray-300 text-gray-400 cursor-not-allowed"
          }`}
          style={{ fontFamily: "DIN Next Rounded" }}
          onClick={handleReset}
          disabled={checked}
        >
          Reset
        </button>
      </div>
    </QuestionPageLayout>
  );
};

export default AksaraLearnPage;
